import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SendProposalModal — mints an owner-facing Design Solution picker link.
 *
 * Shannon picks:
 *   1. Project (dropdown)
 *   2. Which Design Solutions to OFFER on this proposal (multi-select).
 *      Empty = all curated solutions visible. Scoped selection = only those
 *      Solutions appear on the owner's picker (per-apartment-complex control).
 *   3. Owner email + name.
 *
 * Result: a token URL is generated + auto-copied to clipboard. Text/email it.
 */
export default function SendProposalModal({ open, onClose }) {
  const [projects, setProjects] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [offered, setOffered] = useState([]);   // solution ids
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {url, token}
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pr, sl] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
          fetch(`${BACKEND_URL}/api/admin/design-solutions`, { credentials: "include" }).then(r => r.ok ? r.json() : { solutions: [] }),
        ]);
        const projs = Array.isArray(pr) ? pr : (pr.projects || []);
        setProjects(projs);
        setProjectId(prev => prev || (projs[0]?.project_id || ""));
        setSolutions(sl.solutions || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  if (!open) return null;

  const toggleOffer = (id) => {
    setOffered(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const send = async () => {
    if (!projectId || !ownerEmail) { setError("Project and owner email required."); return; }
    setBusy(true); setError(""); setResult(null); setCopied(false);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/proposals/create`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: projectId,
          owner_email: ownerEmail,
          owner_name: ownerName,
          notes,
          offered_solution_ids: offered,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Failed."); setBusy(false); return; }
      const url = `${window.location.origin}/proposal/${d.token}`;
      setResult({ url, token: d.token });
      try { await navigator.clipboard.writeText(url); setCopied(true); } catch { /* clipboard blocked */ }
    } catch { setError("Network error."); }
    setBusy(false);
  };

  const reset = () => {
    setResult(null); setError(""); setCopied(false);
    setOwnerName(""); setOwnerEmail(""); setOffered([]); setNotes("");
  };

  return (
    <div
      data-testid="send-proposal-backdrop"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.75)", backdropFilter: "blur(6px)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="send-proposal-modal"
        style={{ background: "linear-gradient(180deg, #0F1729 0%, #0B1220 100%)", border: "1px solid rgba(90,200,216,0.35)", borderRadius: 14, padding: "1.6rem 1.7rem", maxWidth: 560, width: "100%", maxHeight: "88vh", overflowY: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <p style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", margin: 0 }}>COCKPIT QUICK ACTION</p>
            <h2 style={{ color: "#F3F4F6", fontSize: "1.35rem", fontWeight: 800, margin: "4px 0 6px" }}>🎨 Send Proposal Picker</h2>
            <p style={{ color: "#7E92A8", fontSize: "0.82rem", margin: 0 }}>
              Generate an owner-picker link. Owner picks a Design Solution → Cockpit lights up.
            </p>
          </div>
          <button data-testid="send-proposal-close" onClick={onClose} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        {result ? (
          <div style={{ marginTop: 18 }}>
            <div style={{ background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.35)", borderRadius: 10, padding: "1rem", marginBottom: 12 }}>
              <div style={{ color: "#00DC78", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.06em", marginBottom: 6 }}>
                ✓ LINK READY {copied ? "· COPIED TO CLIPBOARD" : ""}
              </div>
              <div style={{ color: "#ADBED1", fontSize: "0.82rem" }}>Owner picks between {offered.length ? `${offered.length} scoped Solutions` : "all Solutions"}.</div>
            </div>
            <div style={{ background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "0.75rem", marginBottom: 12, wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.78rem", color: "#5AC8D8" }}>
              {result.url}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="send-proposal-copy"
                onClick={async () => { try { await navigator.clipboard.writeText(result.url); setCopied(true); } catch { /* noop */ } }}
                style={btnPrimary}
              >{copied ? "✓ Copied" : "📋 Copy Link"}</button>
              <a
                href={`mailto:${ownerEmail}?subject=Your%20Design%20Proposal%20—%20Moovin%20Forward&body=${encodeURIComponent(`Hi ${ownerName || "there"},\n\nOur team has curated a set of Design Solutions for your unit. Take a look and pick the one that speaks to you:\n\n${result.url}\n\nEach Solution is Section 8, HUD-VASH, and Housing Coalition compliant.\n\nThanks,\nMoovin Forward`)}`}
                data-testid="send-proposal-email"
                style={btnSecondary}
              >✉️ Email Owner</a>
              <button data-testid="send-proposal-another" onClick={reset} style={btnGhost}>+ Another</button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 18, display: "grid", gap: 12 }}>
            <Field label="Project">
              <select data-testid="send-proposal-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} style={inputStyle}>
                {projects.length === 0 && <option value="">No active projects</option>}
                {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.address || p.project_id}</option>)}
              </select>
            </Field>

            <Field label="Owner name">
              <input data-testid="send-proposal-owner-name" type="text" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Jane Property Owner" style={inputStyle} />
            </Field>

            <Field label="Owner email">
              <input data-testid="send-proposal-owner-email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@example.com" style={inputStyle} />
            </Field>

            <Field label={`Design Solutions to offer (${offered.length === 0 ? "all curated" : `${offered.length} scoped`})`}>
              <div style={{ display: "grid", gap: 6 }}>
                {solutions.map(s => {
                  const on = offered.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`send-proposal-offer-${s.id}`}
                      onClick={() => toggleOffer(s.id)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        gap: 12, padding: "0.55rem 0.8rem",
                        background: on ? "rgba(90,200,216,0.10)" : "rgba(15,20,30,0.6)",
                        border: `1px solid ${on ? "#5AC8D8" : "rgba(255,255,255,0.08)"}`,
                        borderRadius: 8, cursor: "pointer", color: "#F3F4F6",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>{s.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#7E92A8" }}>{s.tagline}</div>
                      </div>
                      <span style={{ fontSize: "0.9rem", color: on ? "#5AC8D8" : "#7E92A8", fontWeight: 800 }}>{on ? "✓" : "+"}</span>
                    </button>
                  );
                })}
              </div>
              <p style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 6 }}>Leave all off = owner sees the full curated library.</p>
            </Field>

            <Field label="Notes (optional — internal only)">
              <input data-testid="send-proposal-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Rush turnover · veteran household" style={inputStyle} />
            </Field>

            {error && <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.82rem" }}>{error}</div>}

            <button
              data-testid="send-proposal-generate"
              onClick={send}
              disabled={busy || !projectId || !ownerEmail}
              style={{ ...btnPrimary, opacity: (busy || !projectId || !ownerEmail) ? 0.55 : 1, cursor: (busy || !projectId || !ownerEmail) ? "not-allowed" : "pointer" }}
            >
              {busy ? "Generating…" : "→ Generate Owner Picker Link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", marginBottom: 4, textTransform: "uppercase" }}>{label}</label>
    {children}
  </div>
);

const inputStyle = { width: "100%", background: "rgba(15,20,30,0.6)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 8, padding: "0.7rem 0.85rem", color: "#F3F4F6", fontSize: "0.92rem", outline: "none" };
const btnPrimary = { background: "linear-gradient(90deg, #5AC8D8, #3AA8B8)", color: "#062617", border: "none", borderRadius: 10, padding: "0.85rem 1.2rem", fontWeight: 800, fontSize: "0.95rem", cursor: "pointer", flex: 1, textAlign: "center", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" };
const btnSecondary = { background: "rgba(90,200,216,0.15)", color: "#5AC8D8", border: "1px solid rgba(90,200,216,0.4)", borderRadius: 10, padding: "0.85rem 1rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center" };
const btnGhost = { background: "transparent", color: "#ADBED1", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, padding: "0.85rem 1rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" };
